import { CheckSquareOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Descriptions, Form, InputNumber, Modal, Segmented, Select, Space, TimePicker, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import React from 'react';
import type { Grid, NewAllocation } from '../../DTOS/ResourcePlanning';
import { useBulkChange } from './BulkConfirm';
import { bookedMinutes, dailyCapacity, freeMinutes, hours, isWeekend, projectLabel, useFeedback, useProjects, useTeams } from './shared';

/** One technician and day picked in the planning table; teamId is the planning team of the row it was picked in. */
export interface SelectedCell { userId: string; name: string; teamId: string | null; date: string }
type Mode = 'fixed' | 'fill' | 'slot';
const ROW_TEAM = 'row', NO_TEAM = 'none';
const MAX_ADDITIONS = 5000; // Same limit as BulkAllocations on the server.

/** Allocates one project to every selected technician/day in a single, previewed transaction. */
const CellAllocationModal: React.FC<{ cells: SelectedCell[]; grid: Grid; onClose: () => void; onSaved: () => void }> = ({ cells, grid, onClose, onSaved }) => {
  const { data: projects = [] } = useProjects(); const { data: teams = [] } = useTeams(); const feedback = useFeedback();
  const [form] = Form.useForm(); const bulk = useBulkChange(onSaved);
  const projectId: string | undefined = Form.useWatch('projectId', form); const mode: Mode = Form.useWatch('mode', form) ?? 'fixed';
  const hoursValue: number | null = Form.useWatch('hours', form); const slot: [Dayjs, Dayjs] | undefined = Form.useWatch('slot', form);
  const team: string = Form.useWatch('team', form) ?? ROW_TEAM; const weekends: boolean = Form.useWatch('weekends', form) ?? false;

  const project = projects.find(item => item.id === projectId);
  const onlyWeekends = cells.every(cell => isWeekend(cell.date));
  const weekendCells = cells.filter(cell => isWeekend(cell.date)).length;
  const usable = cells.filter(cell => weekends || onlyWeekends || !isWeekend(cell.date));
  const outside = project ? usable.filter(cell => cell.date < project.startDate || cell.date > project.endDate) : [];
  const existing = new Set(grid.allocations.map(row => `${row.projectId}:${row.userId}:${row.date}`));
  const outsideSet = new Set(outside);
  const duplicates = project ? usable.filter(cell => !outsideSet.has(cell) && existing.has(`${project.id}:${cell.userId}:${cell.date}`)) : [];
  const duplicateSet = new Set(duplicates);
  const booked = bookedMinutes(grid);
  const slotMinutes = slot?.[0] && slot?.[1] ? slot[1].diff(slot[0], 'minute') : 0;
  const minutesFor = (cell: SelectedCell) => mode === 'fill' ? freeMinutes(teams, booked, cell.userId, cell.date, grid.dailyCapacities)
    : mode === 'slot' ? slotMinutes : Math.round((hoursValue || 0) * 60);
  const candidates = project ? usable.filter(cell => !outsideSet.has(cell) && !duplicateSet.has(cell)) : [];
  const full = mode === 'fill' ? candidates.filter(cell => minutesFor(cell) <= 0) : [];
  const fullSet = new Set(full);
  const additions: NewAllocation[] = candidates.filter(cell => !fullSet.has(cell)).map(cell => ({
    projectId: project!.id, userId: cell.userId, date: cell.date, minutes: Math.min(minutesFor(cell), 1440),
    teamId: team === ROW_TEAM ? cell.teamId : team === NO_TEAM ? null : team,
    ...(mode === 'slot' && slot ? { startTime: slot[0].format('HH:mm:ss'), endTime: slot[1].format('HH:mm:ss') } : {}),
  }));
  const total = additions.reduce((sum, row) => sum + row.minutes, 0);
  const people = new Set(cells.map(cell => cell.userId)).size, days = new Set(cells.map(cell => cell.date)).size;
  const skipped = [
    !weekends && !onlyWeekends && weekendCells > 0 && `${weekendCells} zile de weekend nu sunt incluse.`,
    outside.length > 0 && `${outside.length} zile sunt in afara perioadei contractului.`,
    duplicates.length > 0 && `${duplicates.length} zile au deja o alocare pe acest proiect (editati-le din selectie).`,
    full.length > 0 && `${full.length} zile nu mai au ore libere in program.`,
  ].filter(Boolean) as string[];

  const submit = async () => {
    try {
      await form.validateFields();
      if (!additions.length) throw new Error('Nicio alocare noua de creat pentru selectie.');
      if (additions.length > MAX_ADDITIONS) throw new Error(`Selectati cel mult ${MAX_ADDITIONS} de zile odata.`);
      bulk.check({ changes: [], additions }, `Aloca ${project!.number} in ${additions.length} zile`,
        <Space direction='vertical'>
          <Typography.Text>{new Set(additions.map(row => row.userId)).size} tehnicieni · {hours(total)} in total</Typography.Text>
          {skipped.map(text => <Typography.Text key={text} type='secondary'>{text}</Typography.Text>)}
        </Space>);
    } catch (error) { feedback.error(error); }
  };
  const first = cells[0];
  return <>
    <Modal open title={<span><CheckSquareOutlined /> Aloca program pentru selectie</span>} onCancel={onClose} onOk={submit}
      okText='Verifica alocarea' cancelText='Renunta' confirmLoading={bulk.busy} width={640} destroyOnHidden>
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <Descriptions size='small' column={3} bordered items={[
          { key: 'cells', label: 'Zile selectate', children: cells.length },
          { key: 'people', label: 'Tehnicieni', children: people },
          { key: 'days', label: 'Date', children: days },
        ]} />
        <Form form={form} layout='vertical' initialValues={{
          mode: 'fixed', team: ROW_TEAM, weekends: false, hours: dailyCapacity(teams, first.userId, first.date, grid.dailyCapacities) / 60,
        }}>
          <Form.Item name='projectId' label='Proiect' rules={[{ required: true, message: 'Selectati proiectul.' }]}
            extra={project && `Responsabil: ${project.responsibleName || 'Nedesemnat'}`}>
            <Select showSearch optionFilterProp='label' placeholder='Selectati proiectul'
              options={projects.filter(item => item.isActive).map(item => ({ value: item.id, label: projectLabel(item) }))} />
          </Form.Item>
          <Form.Item name='mode' label='Program'>
            <Segmented<Mode> block options={[
              { value: 'fixed', label: 'Ore fixe / zi' },
              { value: 'fill', label: 'Completeaza pana la program' },
              { value: 'slot', label: 'Interval orar' },
            ]} />
          </Form.Item>
          {mode === 'fixed' && <Form.Item name='hours' label='Ore / tehnician / zi' rules={[{ required: true, message: 'Completati orele.' }]}>
            <InputNumber min={0.25} max={24} step={0.25} precision={2} addonAfter='ore' />
          </Form.Item>}
          {mode === 'fill' && <Typography.Paragraph type='secondary'>
            Fiecare zi primeste orele ramase libere din programul tehnicianului (programul echipei de baza sau programul personalizat al zilei), pe toate proiectele.
          </Typography.Paragraph>}
          {mode === 'slot' && <Form.Item name='slot' label='Ora inceput – ora final' rules={[{ validator: (_, value) => !value?.[0] || !value?.[1]
            ? Promise.reject(new Error('Completati ora de inceput si ora finala.'))
            : value[1].isAfter(value[0]) ? Promise.resolve() : Promise.reject(new Error('Ora finala trebuie sa fie dupa ora de inceput, in aceeasi zi.')) }]}>
            <TimePicker.RangePicker format='HH:mm' minuteStep={5} needConfirm={false} order={false} />
          </Form.Item>}
          <Form.Item name='team' label='Echipa operationala' extra='Apartenenta la echipa de baza ramane neschimbata.'>
            <Select options={[
              { value: ROW_TEAM, label: 'Echipa randului din tabel' },
              { value: NO_TEAM, label: 'Fara echipa operationala (alocare directa)' },
              ...teams.filter(item => item.isActive).map(item => ({ value: item.id, label: item.name })),
            ]} />
          </Form.Item>
          {weekendCells > 0 && !onlyWeekends && <Form.Item name='weekends' valuePropName='checked'>
            <Checkbox>Include cele {weekendCells} zile de weekend din selectie</Checkbox>
          </Form.Item>}
        </Form>
        {project && <Alert type={additions.length ? 'info' : 'warning'} showIcon
          message={additions.length ? `${additions.length} alocari noi · ${hours(total)}` : 'Nicio alocare noua pentru selectie.'}
          description={skipped.length > 0 && <Space direction='vertical' size={0}>{skipped.map(text => <span key={text}>{text}</span>)}</Space>} />}
      </Space>
    </Modal>
    {bulk.confirm}
  </>;
};
export default CellAllocationModal;
